import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ClientsAPI, Client, CreateClientDto, UpdateClientDto, ListClientsQuery } from '../api/clients';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

// NEW — a typed error we will reject with
type ApiRejected = { message: string; status?: number };

// NEW — normalize Axios/Nest errors into ApiRejected
const toApiRejected = (err: any): ApiRejected => {
    const status = err?.response?.status ?? err?.statusCode;
    const data = err?.response?.data ?? err;
    const msg =
        typeof data?.message === 'string'
            ? data.message
            : Array.isArray(data?.message)
                ? data.message.join('\n')
                : data?.error || err?.message || 'Request failed';
    return { message: msg, status };
};

interface ClientsState {
    items: Client[];
    selected?: Client | null;
    status: Status;
    error?: string | null;
    query: ListClientsQuery;
}

const initialState: ClientsState = {
    items: [],
    selected: null,
    status: 'idle',
    error: null,
    query: {},
};

export const fetchClients = createAsyncThunk(
    'clients/fetch',
    async (query?: ListClientsQuery) => {
        const data = await ClientsAPI.list(query);
        return { data, query };
    }
);

export const fetchClient = createAsyncThunk(
    'clients/fetchOne',
    async (id: string) => ClientsAPI.getById(id)
);

export const createClient = createAsyncThunk(
    'clients/create',
    async (payload: CreateClientDto) => ClientsAPI.create(payload)
);

export const updateClient = createAsyncThunk(
    'clients/update',
    async ({ id, payload }: { id: string; payload: UpdateClientDto }) =>
        ClientsAPI.update(id, payload)
);

export const deleteClient = createAsyncThunk(
    'clients/delete',
    async (id: string) => {
        await ClientsAPI.remove(id);
        return id;
    }
);

// add thunks (reuse your toApiRejected helper)

export const suspendClient = createAsyncThunk<
    Client,
    string,
    { rejectValue: ApiRejected }
>('clients/suspend', async (id, { rejectWithValue }) => {
    try {
        return await ClientsAPI.suspend(id);
    } catch (err) {
        return rejectWithValue(toApiRejected(err));
    }
});

export const reactivateClient = createAsyncThunk<
    Client,
    string,
    { rejectValue: ApiRejected }
>('clients/reactivate', async (id, { rejectWithValue }) => {
    try {
        return await ClientsAPI.reactivate(id);
    } catch (err) {
        return rejectWithValue(toApiRejected(err));
    }
});

export const sendActivationLink = createAsyncThunk<
    void,
    string,
    { rejectValue: ApiRejected }
>('clients/sendActivationLink', async (email, { rejectWithValue }) => {
    try {
        return await ClientsAPI.sendActivationLink(email);
    } catch (err) {
        return rejectWithValue(toApiRejected(err));
    }
});

const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setQuery(state, action: PayloadAction<ListClientsQuery>) {
            state.query = action.payload;
        },
        clearSelected(state) {
            state.selected = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchClients.pending, state => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload.data;
                state.query = action.payload.query || {};
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to load clients';
            })

            .addCase(fetchClient.fulfilled, (state, action) => {
                state.selected = action.payload;
            })

            .addCase(createClient.fulfilled, (state, action) => {
                state.items.unshift(action.payload);
            })
            .addCase(createClient.rejected, (state, action) => {
                state.error =
                    (action.payload as ApiRejected | undefined)?.message ||
                    action.error.message ||
                    'Create failed';
            })
            .addCase(updateClient.fulfilled, (state, action) => {
                const idx = state.items.findIndex(c => c._id === action.payload._id);
                if (idx >= 0) state.items[idx] = action.payload;
                if (state.selected?._id === action.payload._id) state.selected = action.payload;
            })

            .addCase(deleteClient.fulfilled, (state, action) => {
                state.items = state.items.filter(c => c._id !== action.payload);
                if (state.selected?._id === action.payload) state.selected = null;
            })

            .addCase(suspendClient.fulfilled, (state, action) => {
                const idx = state.items.findIndex(c => c._id === action.payload._id);
                if (idx >= 0) state.items[idx] = action.payload;
            })
            .addCase(reactivateClient.fulfilled, (state, action) => {
                const idx = state.items.findIndex(c => c._id === action.payload._id);
                if (idx >= 0) state.items[idx] = action.payload;
            });

    },
});

export const { setQuery, clearSelected } = clientsSlice.actions;
export default clientsSlice.reducer;